import React, { Component, PropTypes } from 'react'
import Helmet from 'react-helmet'
import { Link as RouterLink } from 'react-router'
import { prefixLink } from 'gatsby-helpers'
import { config } from 'config'
import throttle from 'lodash.throttle'
import CountUp from 'countup.js'

import { colors, breakpoints } from '../constants/theme'

import Section from '../components/Section'
import Column from '../components/Column'
import Row from '../components/Row'
import Spacer from '../components/Spacer'
import Container from '../components/Container'
import Page from '../components/Page'
import Icon from '../components/Icon'
import Image from '../components/Image'

import Heading1 from '../styleguide/Heading1'
import Heading2 from '../styleguide/Heading2'
import Heading3 from '../styleguide/Heading3'
import Heading4 from '../styleguide/Heading4'
import Heading5 from '../styleguide/Heading5'
import Heading6 from '../styleguide/Heading6'
import Paragraph from '../styleguide/Paragraph'
import Navigation from '../styleguide/Navigation'
import Link from '../styleguide/Link'
import LinkList from '../styleguide/LinkList'
import Footer from '../styleguide/Footer'
import Subtext from '../styleguide/Subtext'
import Services from '../styleguide/Services'
import Quote from '../styleguide/Quote'
import QuoteAuthor from '../styleguide/QuoteAuthor'
import Button from '../styleguide/Button'
import Line from '../styleguide/Line'

import FelaProvider from '../containers/FelaProvider'

const { min1920, min1440, min1024, min768, min480, min360 } = breakpoints

const metaData = [
  {
    name: 'description',
    content: 'Ninja Concept Web Applications'
  },
  {
    name: 'keywords',
    content: 'web agency'
  }
]

const Navigator = () => (
  <Page
    id="navigation"
    style={{
      position: 'fixed',
      zIndex: 10,
      transformStyle: 'preserve-3d'
    }}
  >
    <Container color="white" flex="0 0 auto">
      <Helmet title="Ninja Concept | Sophisticated web &amp; mobile applications" meta={metaData} />
      <Row alignItems="center" justifyContent="flex-start" style={{ alignItems: 'flex-start' }}>
        <Column
          style={{
            flexDirection: 'row',
            justifyContent: 'flex-start'
          }}
          alignItems="center"
        >
          <img
            src={prefixLink('/images/logo/logo_ninjaconcept_horizontal.svg')}
            style={{ cursor: 'pointer' }}
            onClick={() => (window.location.pathname = '/')}
          />
          <Spacer size={20} />
          <RouterLink to={prefixLink('/')}>
            <Navigation
              bold
              color="white"
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                alignSelf: 'flex-start'
              }}
            >
              <i
                className="fa fa-angle-left"
                aria-hidden="true"
                style={{
                  fontSize: 30,
                  marginTop: -4
                }}
              />
              <Spacer size={6} /> HOME
            </Navigation>
          </RouterLink>
        </Column>
      </Row>
    </Container>
  </Page>
)

const members = {
  ms: {
    firstname: 'Marco',
    lastname: 'Sehrer',
    job: 'Executive Director / Software Architect'
  },
  nr: {
    firstname: 'Nadine',
    lastname: 'Rothweiler',
    job: 'Office Manager'
  },
  dw: {
    firstname: 'Dominika',
    lastname: 'Wloka',
    job: 'User Experience Design / Web Consultant'
  },
  fw: {
    firstname: 'Ferdinand',
    lastname: 'Werthwein',
    job: 'User Experience Designer'
  },
  mso: {
    firstname: 'Michael',
    lastname: 'Sowka',
    job: 'DevOps Consultant / Cloud Engineer'
  },
  jl: {
    firstname: 'Johannes',
    lastname: 'Linowski',
    job: 'Web Consultant / Software Engineer'
  },
  fs: {
    firstname: 'Frederik',
    lastname: 'Schroff',
    job: 'Web Consultant / Software Engineer'
  },
  rf: {
    firstname: 'Robin',
    lastname: 'Frischmann',
    job: 'Software Engineer'
  },
  jw: {
    firstname: 'Jan-Philipp',
    lastname: 'Willem',
    job: 'Software Engineer'
  },
  fm: {
    firstname: 'Felix',
    lastname: 'Maurer',
    job: 'Software Engineer'
  },
  ts: {
    firstname: 'Tobias',
    lastname: 'Sturm',
    job: 'Software Engineer'
  }
}

const Member = ({ data, shortname }) => (
  <Column
    height="auto"
    widths={{
      [min768]: '50%',
      [min1024]: '33%',
      [min1440]: '25%'
    }}
    style={{
      height: 450,
      overflow: 'hidden',
      justifyContent: 'flex-start'
    }}
    center
  >
    <Image base={prefixLink(`/images/avatar/avatar_${shortname}`)} />
    <Spacer size={24} />
    <Heading5>
      {data.title ? `${data.title} ` : ''}
      {`${data.firstname} `}
      {data.lastname}
    </Heading5>
    <Paragraph textAlign="center">
      <span dangerouslySetInnerHTML={{ __html: data.job.replace('/', '/</br>') }} />
    </Paragraph>
  </Column>
)

const statistics = {
  drinks: {
    comparison: true,
    title: 'Kaffee vs. Tee',
    value: [8, 4]
  },
  age: {
    title: '\xD8 Ninja Alter',
    value: [30]
  },
  meat: {
    comparison: true,
    title: 'Tofu vs. Steak',
    value: [3, 9]
  },
  languages: {
    comparison: true,
    title: 'Code- vs. Fremdsprachen',
    value: [25, 9]
  }
}

const Statistic = ({ type, data }) => (
  <Column
    height="auto"
    widths={{
      [min768]: '50%',
      [min1440]: '25%'
    }}
    style={{ padding: '10px' }}
    alignItems="center"
  >
    {data.comparison ? (
      <Section row alignSelf="stretch" center>
        <Heading1
          color={colors.highlight}
          style={{
            fontWeight: 400,
            paddingTop: 8,
            fontSize: 60
          }}
          data-parallax-count={type}
        >
          0
        </Heading1>
        <Heading1
          color={colors.highlight}
          style={{
            fontWeight: 400,
            paddingTop: 8,
            fontSize: 60
          }}
        >
          :
        </Heading1>
        <Heading1
          color={colors.highlight}
          style={{
            fontWeight: 400,
            paddingTop: 8,
            fontSize: 60
          }}
          data-parallax-count={type}
        >
          0
        </Heading1>
        <Spacer size={15} />
        <Image base={prefixLink(`/images/icons/${type}`)} />
      </Section>
    ) : (
        <Section row alignSelf="stretch" center>
          <Heading1
            color={colors.highlight}
            style={{
              fontWeight: 400,
              paddingTop: 8,
              fontSize: 60
            }}
            data-parallax-count={type}
          >
            0
        </Heading1>
          <Spacer size={15} />
          <Image base={prefixLink(`/images/icons/${type}`)} />
        </Section>
      )}
    <Spacer size={15} />
    <Paragraph textAlign="center">{data.title}</Paragraph>
    <Spacer size={24} />
    <Line width="100px" stroke={4} data-parallax-line color={colors.background} />
    <Spacer size={100} sizes={{ [min1920]: 0 }} />
  </Column>
)

const Team = (props, { width, retina }) => (
  <FelaProvider>
    <Navigator />
    <Page
      data-scroll-scope="landing"
      bgCover
      bgImage={prefixLink('/images/images/team_hero_bg')}
      id="headerImage"
      bgRetina={retina}
      bgImages={{
        [min360]: min360,
        [min480]: min480,
        [min768]: min768,
        [min1024]: min1024,
        [min1440]: min1440
      }}
      height="100vh"
      heights={{ [min768]: '55vh' }}
    >
      <Container color="white" flex="1 0 auto">
        <Section flex center>
          <Helmet
            title="Ninja Concept | Sophisticated web &amp; mobile applications"
            meta={metaData}
          />
          <Row
            columns={{
              [min768]: 8,
              [min1920]: 6
            }}
          >
            <Column
              sizes={{
                [min768]: 8,
                [min1920]: 6
              }}
            >
              <Heading1 textAlign="center">Meet the Team!</Heading1>
            </Column>
          </Row>
          <Spacer
            size={11}
            sizes={{
              [min480]: 14,
              [min768]: 18,
              [min1440]: 24
            }}
          />
          <Row
            columns={{
              [min1024]: 10,
              [min1920]: 8
            }}
          >
            <Column>
              <Subtext textAlign="center">
                Wir sind ein Team aus kreativen Köpfen, Web-Enthusiasten und Kaffee-Liebhabern.
                Zusammen verfügen wir über jahrzehntelange Erfahrung in der Entwicklung von Web- und
                Mobil-Applikationen mit großartiger User Experience.
              </Subtext>
              <Spacer size="6%" />
            </Column>
          </Row>
        </Section>
      </Container>
    </Page>
    <Page bgColor="white">
      <Spacer
        size={60}
        sizes={{
          [min768]: 80,
          [min1440]: 120,
          [min1920]: 160
        }}
      />
      <Container center color={colors.text} flex="1 0 auto">
        <Row
          center
          style={{ flexDirection: 'row' }}
          columns={{
            [min768]: 10,
            [min1024]: 12
          }}
        >
          {Object.keys(members).map(member => (
            <Member key={member} shortname={member} data={members[member]} />
          ))}
        </Row>
        <Line stroke={1} width="100%" color="rgba(0,0,0, 0.1)" />
        <Spacer size={100} />
      </Container>
      <Container center color={colors.text} flex="1 0 auto">
        <Row center columns={{ [min768]: 12 }}>
          {Object.keys(statistics).map(statistic => (
            <Statistic type={statistic} key={statistic} data={statistics[statistic]} />
          ))}
        </Row>
        <Spacer size={20} sizes={{ [min1920]: 100 }} />
      </Container>
    </Page>
    <Image
      style={{ width: '100vw' }}
      excludeMin
      jpg
      base={prefixLink('/images/images/team')}
      sizes={[min360, min480, min768, min1024, min1440]}
    />
    <Page height="0%" bgColor={colors.background}>
      <Container center color="white" flex="1 0 auto">
        <Spacer size={120} sizes={{ [min768]: 100 }} />
        <Section center flex alignSelf="stretch">
          <Heading2 color={colors.highlight}>Join the team!</Heading2>
          <Spacer
            size={28}
            sizes={{
              [min768]: 20,
              [min1024]: 28,
              [min1024]: 24
            }}
          />
          <a href="mailto:contact@ninjaconcept.com?subject=Ruby%20Elixir%20Ninja">
            <LinkList> Ruby / Elixir Ninja </LinkList>
          </a>
          <a href="mailto:contact@ninjaconcept.com?subject=Elm%20Haskell%20Ninja">
            <LinkList> Elm / Haskell Ninja </LinkList>
          </a>
          <a href="mailto:contact@ninjaconcept.com?subject=JS%20Full%20Stack%20Ninja">
            <LinkList> JS Full Stack Ninja </LinkList>
          </a>
          <a href="mailto:contact@ninjaconcept.com?subject=DevOps%20Cloud%20Ninja">
            <LinkList> DevOps / Cloud Ninja </LinkList>
          </a>
        </Section>
      </Container>
    </Page>
    <Spacer size={100} />
    <Line width="100%" stroke={1} color="rgba(255, 255, 255, 0.4)" />
    <Page height="0%" data-scroll-scope="contact" bgColor={colors.background}>
      <Spacer
        size={120}
        sizes={{
          [min768]: 100,
          [min1024]: 120,
          [min1440]: 160
        }}
      />
      <Container color="white" flex="1 0 auto">
        <Row columns={{ [min768]: 12 }} center>
          <Column sizes={{ [min768]: 12 }}>
            <Section alignSelf="stretch" flex="1 0 auto" center>
              <Image svg base={prefixLink('/images/logo/logo_ninjaconcept_horizontal')} />
            </Section>
            <Spacer size={64} />
            <Footer bold textAlign="center">
              © 2016-2024 NinjaConcept GmbH
            </Footer>
            <Footer textAlign="center">
              The design of ninjaconcept is a registered trademark of NinjaConcept GmbH.
              <br />Hardtstraße 37A, 76185 Karlsruhe, Germany
            </Footer>
            <Section flex="1 0 auto" alignSelf="stretch" center row columnOnSmall>
              <Footer bold>
                <a href="tel:+49 721 180 352 30">+49 721 180 352 30</a>
              </Footer>
              <Spacer size={0} sizes={{ [min768]: 18 }} />
              <Footer bold>
                <a href="mailto:contact@ninjaconcept.com">contact@ninjaconcept.com</a>
              </Footer>
              <Spacer size={0} sizes={{ [min768]: 18 }} />
              <RouterLink to={prefixLink('/impressum/')}>
                <Footer bold>Impressum</Footer>
              </RouterLink>
              <Spacer size={0} sizes={{ [min768]: 18 }} />
              <RouterLink to={prefixLink('/datenschutz/')}>
                <Footer bold>Datenschutzerklärung</Footer>
              </RouterLink>
            </Section>
          </Column>
        </Row>
      </Container>
      <Spacer
        size={60}
        sizes={{
          [min1440]: 68,
          [min1920]: 76
        }}
      />
    </Page>
  </FelaProvider>
)

Team.contextTypes = {
  width: PropTypes.number,
  retina: PropTypes.bool
}

class DeviceProvider extends Component {
  static childContextTypes = {
    width: PropTypes.number,
    retina: PropTypes.bool
  }

  constructor(props, context) {
    super(props, context)
    this.distance = 0
    this.state = {
      width: 0,
      retina: false
    }
  }

  updateDevice = () => {
    this.setState({
      width: window.innerWidth,
      retina: window.devicePixelRatio > 1.5
    })
  }

  draw = () => {
    window.requestAnimationFrame(this.draw)
    this.animate()
  }

  animate = () => {
    const scrollTop = this.mount.scrollTop

    const offset =
      scrollTop > this.mount.scrollHeight - window.innerHeight ? 0 : this.lastScrollTop - scrollTop

    const newDirection = offset < 0 ? 'down' : offset > 0 ? 'up' : this.direction

    if (newDirection === this.direction && Date.now() >= window.IS_SCROLLING) {
      this.distance += Math.abs(offset)
    } else {
      this.distance = 0
    }

    if (Date.now() > window.LAST_SCROLL + 1000) {
      this.distance = 0
    }

    this.direction = newDirection

    this.lastScrollTop = scrollTop
    const scrollPercentage = scrollTop / (this.headerImage.offsetHeight - 40)

    this.nav.style.backgroundColor = `rgba(22, 24, 28, ${Math.max(
      0,
      Math.min(1.0, scrollPercentage)
    )})`

    if (this.direction === 'up' && this.distance > 200) {
      (this.nav.style.transition = '200ms transform linear'),
        (this.nav.style.transform = 'translate3d(0, 0, 0)')
    }

    if (
      ((this.direction === 'down' && this.distance > 70) ||
        Date.now() > window.LAST_SCROLL + 3500) &&
      scrollTop > this.headerImage.offsetHeight - 40
    ) {
      (this.nav.style.transition = '200ms transform linear'),
        (this.nav.style.transform = `translate3d(0, -${this.nav.offsetHeight}px, 0)`)
    }
  }

  scrollEvent = (e) => {
    const scrollTop = this.mount.scrollTop

    const idCounts = {
      drinks: -1,
      age: -1,
      meat: -1,
      languages: -1
    }

    window.LAST_SCROLL = Date.now()

    const allScrollElements = document.querySelectorAll('[data-parallax-count]')

    for (let i = 0; i < allScrollElements.length; ++i) {
      const element = allScrollElements[i]
      const id = element.attributes['data-parallax-count'].value
      const count = idCounts[id] + 1
      idCounts[id] = count

      const elementStart = element.offsetTop - window.innerHeight

      if (scrollTop >= elementStart + window.innerHeight / 5 && !this.triggered[id + count]) {
        this.triggered[id + count] = true
        const demo = new CountUp(element, 0, statistics[id].value[count], 0, 2, {
          useEasing: true,
          useGrouping: true,
          separator: '.',
          decimal: ',',
          prefix: '',
          suffix: ''
        })

        demo.start()
      }
    }

    const allLineElements = document.querySelectorAll('[data-parallax-line]')

    for (let i = 0; i < allLineElements.length; ++i) {
      const element = allLineElements[i]
      const elementStart = element.offsetTop - window.innerHeight

      if (scrollTop >= elementStart + window.innerHeight / 5) {
        element.classList.remove('width-0')
      }
    }
  }

  getChildContext() {
    return {
      width: this.state.width,
      retina: this.state.retina
    }
  }

  componentDidMount() {
    this.mount = document.getElementById('react-mount')
    this.nav = document.getElementById('navigation')
    this.nav.classList.add('animated')
    this.headerImage = document.getElementById('headerImage')
    this.lastScrollTop = this.mount.scrollTop
    this.triggered = {}

    const allLineElements = document.querySelectorAll('[data-parallax-line]')

    for (let i = 0; i < allLineElements.length; ++i) {
      const element = allLineElements[i]
      element.classList.add('width-0')
    }

    window.IS_SCROLLING = Date.now()

    this.draw()
    this.updateDevice()

    this.mount.addEventListener('scroll', e => throttle(() => this.scrollEvent(e), 20)())

    window.addEventListener('resize', () => {
      this.updateDevice()
    })
  }

  render() {
    return this.props.children
  }
}

export default () => (
  <DeviceProvider>
    <Team />
  </DeviceProvider>
)



// WEBPACK FOOTER //
// ./team.js